import {
  Button,
  Input,
  Select,
  Checkbox,
  List,
  Row,
  Col,
  Flex,
  Modal,
  Typography,
} from "antd";
import { Form } from "antd";
import { useMemo, createContext } from "react";

import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import DragHandle from "@/components/ui/DragHandle";
import CloseIcon from "@/icons/CloseIcon";
import PlusIcon from "@/icons/PlusIcon";

const TYPE_OPTIONS = [
  { value: "text", label: "Строка" },
  { value: "textarea", label: "Текст" },
  { value: "float", label: "Дробное" },
  { value: "integer", label: "Целое" },
  { value: "select", label: "Выбор из списка" },
  { value: "date", label: "Дата" },
  { value: "moment", label: "Момент" },
  { value: "money", label: "Деньги" },
  { value: "checkbox", label: "Чекбокс" },
];

const PLACEHOLDER_TYPES = ["text", "textarea", "float", "integer", "money"];

const SortableListItemContext = createContext({});

const FieldsList = ({ templateFieldIds = new Set(), groupName, isEditing }) => {
  const SortableListItem = ({ itemKey, ...rest }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      setActivatorNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: itemKey });

    const style = {
      transform: CSS.Translate.toString(transform),
      transition,
      ...(isDragging ? { zIndex: 9999 } : {}),
    };

    const ctx = useMemo(
      () => ({ setActivatorNodeRef, listeners, attributes }),
      [setActivatorNodeRef, listeners, attributes],
    );

    return (
      <SortableListItemContext.Provider value={ctx}>
        <List.Item ref={setNodeRef} style={style} {...rest} />
      </SortableListItemContext.Provider>
    );
  };

  const SortableOptionItem = ({ itemKey, children }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      setActivatorNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: itemKey });

    const style = {
      transform: CSS.Translate.toString(transform),
      transition,
      paddingBlock: 5,
      ...(isDragging ? { zIndex: 9999 } : {}),
    };

    const ctx = useMemo(
      () => ({ setActivatorNodeRef, listeners, attributes }),
      [setActivatorNodeRef, listeners, attributes],
    );

    return (
      <SortableListItemContext.Provider value={ctx}>
        <div ref={setNodeRef} style={style}>
          {children}
        </div>
      </SortableListItemContext.Provider>
    );
  };

  return (
    <Form.List name={groupName}>
      {(fields, { add, remove, move }) => {
        const showFieldUsedWarning = () => {
          Modal.warning({
            title: "Невозможно удалить поле",
            content: (
              <Typography.Text>
                Данное поле используется в шаблоне названия задачи
              </Typography.Text>
            ),
            okText: "Закрыть",
          });
        };

        return (
          <>
            {fields.length > 0 && (
              <DndContext
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={({ active, over }) => {
                  if (!over || active.id === over.id) return;

                  const oldIndex = fields.findIndex((f) => f.key === active.id);
                  const newIndex = fields.findIndex((f) => f.key === over.id);

                  move(oldIndex, newIndex);
                }}
              >
                <SortableContext
                  items={fields.map((f) => f.key)}
                  strategy={verticalListSortingStrategy}
                >
                  {fields.map(({ key, name: idx, ...restField }) => {
                    const { key: _omitKey, ...fieldProps } = restField;
                    return (
                      <SortableListItem key={key} itemKey={key}>
                        <Flex vertical gap={10} style={{ width: "100%" }}>
                          <Row
                            gutter={[8, 15]}
                            style={{ width: "100%", marginTop: 20 }}
                            align="top"
                          >
                            <Col xs={24} md={14}>
                              <Row gutter={8} align="middle">
                                <Col flex="none">
                                  {isEditing && (
                                    <DragHandle
                                      style={{ marginTop: 6 }}
                                      context={SortableListItemContext}
                                    />
                                  )}
                                </Col>
                                <Col flex="auto">
                                  <Form.Item
                                    {...fieldProps}
                                    name={[idx, "uuid"]}
                                    hidden
                                  >
                                    <Input />
                                  </Form.Item>

                                  <Form.Item
                                    {...fieldProps}
                                    name={[idx, "name"]}
                                    rules={[
                                      {
                                        required: true,
                                        message:
                                          "Пожалуйста, введите название поля",
                                      },
                                    ]}
                                    style={{ marginBottom: 0 }}
                                  >
                                    <Input
                                      placeholder="Название поля"
                                      disabled={!isEditing}
                                    />
                                  </Form.Item>
                                </Col>

                                {isEditing && (
                                  <Col flex="none" xs={1} md={0}>
                                    <Form.Item noStyle shouldUpdate>
                                      {({ getFieldValue }) => (
                                        <Button
                                          type="text"
                                          icon={<CloseIcon />}
                                          onClick={() => {
                                            const fieldUuid = getFieldValue([
                                              groupName,
                                              idx,
                                              "uuid",
                                            ]);

                                            if (
                                              fieldUuid &&
                                              templateFieldIds.has(fieldUuid)
                                            ) {
                                              showFieldUsedWarning();
                                              return;
                                            }

                                            remove(idx);
                                          }}
                                        />
                                      )}
                                    </Form.Item>
                                  </Col>
                                )}
                              </Row>
                            </Col>

                            <Col xs={24} md={5}>
                              <Form.Item noStyle shouldUpdate>
                                {({ getFieldValue }) => {
                                  const id = getFieldValue([
                                    groupName,
                                    idx,
                                    "uuid",
                                  ]);
                                  const isExisting = Boolean(id);

                                  return (
                                    <Form.Item
                                      {...fieldProps}
                                      name={[idx, "type"]}
                                      style={{ marginBottom: 0 }}
                                      rules={[
                                        {
                                          required: true,
                                          message:
                                            "Пожалуйста, введите тип поля",
                                        },
                                      ]}
                                    >
                                      <Select
                                        disabled={!isEditing || isExisting}
                                        placeholder="Тип"
                                        options={TYPE_OPTIONS}
                                      />
                                    </Form.Item>
                                  );
                                }}
                              </Form.Item>
                            </Col>

                            <Col
                              xs={24}
                              md={3}
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <Form.Item
                                {...fieldProps}
                                name={[idx, "is_required"]}
                                valuePropName="checked"
                                // initialValue={false}
                                style={{ marginBottom: 0 }}
                              >
                                <Checkbox disabled={!isEditing}>
                                  Обязательное
                                </Checkbox>
                              </Form.Item>
                            </Col>

                            {isEditing && (
                              <Col
                                flex="40px"
                                xs={0}
                                md={1}
                                style={{
                                  textAlign: "right",
                                  alignItems: "center",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <Form.Item noStyle shouldUpdate>
                                  {({ getFieldValue }) => (
                                    <Button
                                      type="text"
                                      icon={<CloseIcon />}
                                      onClick={() => {
                                        const fieldUuid = getFieldValue([
                                          groupName,
                                          idx,
                                          "uuid",
                                        ]);

                                        if (
                                          fieldUuid &&
                                          templateFieldIds.has(fieldUuid)
                                        ) {
                                          showFieldUsedWarning();
                                          return;
                                        }

                                        remove(idx);
                                      }}
                                    />
                                  )}
                                </Form.Item>
                              </Col>
                            )}
                          </Row>

                          <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue, setFieldValue }) => {
                              const type = getFieldValue([
                                groupName,
                                idx,
                                "type",
                              ]);
                              const placeholder = getFieldValue([
                                groupName,
                                idx,
                                "placeholder",
                              ]);

                              const canHavePlaceholder =
                                PLACEHOLDER_TYPES.includes(type);
                              const isPlaceholderVisible =
                                placeholder !== undefined &&
                                placeholder !== null;

                              if (!canHavePlaceholder) return null;

                              return (
                                <Flex style={{ paddingLeft: 30 }}>
                                  {isPlaceholderVisible ? (
                                    <Flex
                                      gap={8}
                                      align="center"
                                      style={{ width: "100%" }}
                                    >
                                      <Form.Item
                                        {...fieldProps}
                                        name={[idx, "placeholder"]}
                                        style={{
                                          marginBottom: 0,
                                          maxWidth: 500,
                                          width: "100%",
                                        }}
                                      >
                                        <Input
                                          placeholder="Плейсхолдер"
                                          disabled={!isEditing}
                                          allowClear
                                        />
                                      </Form.Item>
                                    </Flex>
                                  ) : (
                                    isEditing && (
                                      <Button
                                        type="link"
                                        icon={<PlusIcon />}
                                        onClick={() =>
                                          setFieldValue(
                                            [groupName, idx, "placeholder"],
                                            "",
                                          )
                                        }
                                        style={{
                                          padding: 0,
                                          color: "#bcbcbc",
                                          alignSelf: "start",
                                        }}
                                      >
                                        Добавить плейсхолдер
                                      </Button>
                                    )
                                  )}
                                </Flex>
                              );
                            }}
                          </Form.Item>

                          <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => {
                              const type = getFieldValue([
                                groupName,
                                idx,
                                "type",
                              ]);
                              if (type !== "select") return null;

                              return (
                                <Form.List name={[idx, "options"]}>
                                  {(options, { add, remove, move }) => (
                                    <DndContext
                                      modifiers={[restrictToVerticalAxis]}
                                      onDragEnd={({ active, over }) => {
                                        if (!over || active.id === over.id)
                                          return;

                                        const oldIndex = options.findIndex(
                                          (o) => o.key === active.id,
                                        );
                                        const newIndex = options.findIndex(
                                          (o) => o.key === over.id,
                                        );

                                        move(oldIndex, newIndex);
                                      }}
                                    >
                                      <SortableContext
                                        items={options.map((o) => o.key)}
                                        strategy={verticalListSortingStrategy}
                                      >
                                        <Flex
                                          vertical
                                          gap={4}
                                          style={{ paddingLeft: 30 }}
                                          align="start"
                                        >
                                          {options.map((opt) => {
                                            const {
                                              key: _omitKey,
                                              ...optProps
                                            } = opt;
                                            return (
                                              <SortableOptionItem
                                                key={opt.key}
                                                itemKey={opt.key}
                                              >
                                                <Flex gap={8} align="center">
                                                  {isEditing && (
                                                    <DragHandle
                                                      context={
                                                        SortableListItemContext
                                                      }
                                                    />
                                                  )}

                                                  <Form.Item
                                                    {...optProps}
                                                    name={[opt.name, "uuid"]}
                                                    hidden
                                                  >
                                                    <Input />
                                                  </Form.Item>

                                                  <Form.Item
                                                    {...optProps}
                                                    name={[opt.name, "value"]}
                                                    style={{ marginBottom: 0 }}
                                                    rules={[
                                                      {
                                                        required: true,
                                                        message:
                                                          "Введите название опции",
                                                      },
                                                    ]}
                                                  >
                                                    <Input
                                                      placeholder="Опция"
                                                      disabled={!isEditing}
                                                    />
                                                  </Form.Item>

                                                  {isEditing && (
                                                    <Button
                                                      type="text"
                                                      icon={<CloseIcon />}
                                                      onClick={() =>
                                                        remove(opt.name)
                                                      }
                                                    />
                                                  )}
                                                </Flex>
                                              </SortableOptionItem>
                                            );
                                          })}

                                          {isEditing && (
                                            <Button
                                              type="link"
                                              icon={<PlusIcon />}
                                              onClick={() => add()}
                                              style={{
                                                padding: 0,
                                                color: "#bcbcbc",
                                                alignSelf: "start",
                                              }}
                                            >
                                              Добавить опцию списка
                                            </Button>
                                          )}
                                        </Flex>
                                      </SortableContext>
                                    </DndContext>
                                  )}
                                </Form.List>
                              );
                            }}
                          </Form.Item>
                        </Flex>
                      </SortableListItem>
                    );
                  })}
                </SortableContext>
              </DndContext>
            )}

            {isEditing && (
              <Button
                type="link"
                icon={<PlusIcon />}
                onClick={() => add({ is_required: false })}
                style={{ padding: 0, color: "#777777", marginTop: 20 }}
              >
                Добавить поле
              </Button>
            )}
          </>
        );
      }}
    </Form.List>
  );
};

export default FieldsList;
